import axios from "axios";
import config from "../../appConfig";
import { $removeFromLocal } from ".";
import { $getAuthHeaders, $getFromLocal, $setToLocal, $removeFromCookies } from '@/utils';
import { log as debugLog } from '@/utils/debugLogger';
import { requestRegistry } from './http/RequestRegistry';
import Vue from 'vue';
import { syncBaselinePendingMessage } from './http/syncBaselinePending';

class HttpService {
  constructor() {
    this.appConfig=config;
    const headers = $getAuthHeaders();
    const service = axios.create({
      baseURL: config.API_URL,
      headers: headers,
      timeout: 0,
      paramsSerializer: {
        indexes: null // Serialize arrays as repeated keys (ASP.NET Core compatible)
      }
    });

    // Initialize request registry (sets up page unload handlers)
    requestRegistry.init();

    // Request interceptors (executed in order: registration → auth token)
    //
    // `synchronous: true`: both interceptors are plain synchronous functions, so axios may run
    // them in the calling tick and send the request at once. Without it axios runs them as
    // promise steps, and Vue re-renders the "loading" state BEFORE the request leaves
    // (measured 77–414 ms per Tag Register click in the dev build).
    // Keep every request interceptor synchronous: axios goes sync only when ALL of them are
    // flagged, and silently falls back to the slower async path otherwise (e.g. the dev-only
    // aiInspectMock interceptor). An interceptor that must await something (token refresh, ...)
    // has to drop this flag knowingly.
    service.interceptors.request.use(this.handleRequestRegistration, (error) => Promise.reject(error), { synchronous: true });
    service.interceptors.request.use(this.handleRequest, (error) => Promise.reject(error), { synchronous: true });

    // Response interceptors (cleanup → success/error handling)
    service.interceptors.response.use(
      this.handleResponseCleanup,
      this.handleResponseCleanupError.bind(this)
    );
    service.interceptors.response.use(this.handleSuccess, this.handleError.bind(this));

    this.service = service;
    this.isRefreshing = false;
    this.isLoggingOut = false;
    this.failedQueue = [];
  }

  /**
   * Process queued requests after token refresh
   */
  processQueue = (error, token = null) => {
    this.failedQueue.forEach(prom => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });
    this.failedQueue = [];
  };

  /**
   * Request interceptor - registers request with the RequestRegistry
   * Enables automatic cancellation on navigation/unload
   *
   * Config options:
   * - signal: If already provided, skip auto-registration (manual control)
   * - critical: If true, request won't be auto-cancelled on navigation
   * - _skipRegistry: Internal flag for retry requests
   */
  handleRequestRegistration = (config) => {
    // Skip registration if:
    // 1. Request already has a signal (caller is managing cancellation)
    // 2. Request is a retry (already registered)
    // 3. Explicitly skipped
    if (config.signal || config._retry || config._skipRegistry) {
      return config;
    }

    // Register the request and attach the abort signal
    const { controller, requestId } = requestRegistry.register(config);
    config.signal = controller.signal;
    config._requestId = requestId;

    return config;
  };

  /**
   * Request interceptor - adds current access token to headers
   */
  handleRequest = (config) => {
    const token = $getFromLocal('accessToken', false, null);
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    // Add client type header for the API to know we're a web client
    //config.headers['X-Client-Type'] = 'web';
    return config;
  };

  /**
   * Response interceptor - unregisters completed requests from registry
   */
  handleResponseCleanup = (response) => {
    const requestId = response.config?._requestId;
    if (requestId) {
      requestRegistry.unregister(requestId);
    }
    return response;
  };

  /**
   * Response error interceptor - unregisters failed requests from registry
   * Must re-throw the error to allow handleError to process it
   */
  handleResponseCleanupError = (error) => {
    const requestId = error.config?._requestId;
    if (requestId) {
      requestRegistry.unregister(requestId);
    }
    return Promise.reject(error);
  };

  handleToken() {
    // This method is called after login to update the default headers
    // With the new interceptor-based approach, we don't need to set defaults
    // The request interceptor will add the token from localStorage
    this.service.defaults.headers = $getAuthHeaders();
  }

  handleSuccess = (response) => {
    return response;
  };

  handleError = async (error) => {
    if (error?.name === 'CanceledError') {
      return Promise.reject({ ...error });
    }

    // Axios errors carry request headers and config.data. Logging the raw object can expose
    // bearer tokens, refresh tokens, one-time approval grants, or other sensitive payloads.
    debugLog({
      name: error?.name,
      message: error?.message,
      code: error?.code,
      status: error?.response?.status,
      method: error?.config?.method,
      url: error?.config?.url,
      traceId: error?.response?.data?.traceId,
    });
    const originalRequest = error.config;

    // Handle 401 errors with automatic token refresh
    if (error.response?.status === 401) {
      const isAVA =error.request?.responseURL?.includes(this.appConfig.API_URL);

      if (!isAVA) {
        return Promise.reject({ ...error });
      }

      const data = error.response.data;

      // If explicitly told to relogin, don't try to refresh
      if (data && data.ErrorType === 'Relogin') {
        this.performLogout();
        return Promise.reject({ ...error });
      }

      // Don't retry refresh token requests or requests already retried
      if (originalRequest.url?.includes('/Auth/refresh') || originalRequest._retry) {
        // Refresh failed, force logout
        this.performLogout();
        return Promise.reject({ ...error });
      }

      // If already refreshing, queue this request
      if (this.isRefreshing) {
        return new Promise((resolve, reject) => {
          this.failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          return this.service(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      this.isRefreshing = true;

      try {
        // Get refresh token from localStorage
        const refreshToken = $getFromLocal('refreshToken', false, null);

        if (!refreshToken) {
          // Check if user was previously logged in (session expired vs never logged in)
          const user = $getFromLocal('user', true, null);

          this.processQueue(new Error('No refresh token available'), null);
          this.isRefreshing = false;

          if (user) {
            // User WAS logged in but token is gone - session expired, perform logout
            this.performLogout();
          }
          // If no user, they were never logged in - don't logout (prevents loop on auth pages)

          return Promise.reject(error);
        }

        // Attempt to refresh the token
        const response = await this.service.post('api/Auth/refresh', { refreshToken });

        const { accessToken, refreshToken: newRefreshToken, accessTokenExpiresAt, expiresIn } = response.data;

        // Store new tokens
        $setToLocal('accessToken', accessToken, false);
        $setToLocal('accessTokenExpiresAt', accessTokenExpiresAt, false);
        $setToLocal('tokenExpiresIn', expiresIn, false);

        // Store new refresh token (token rotation)
        if (newRefreshToken) {
          $setToLocal('refreshToken', newRefreshToken, false);
        }

        // Update default headers
        this.service.defaults.headers['Authorization'] = `Bearer ${accessToken}`;

        // Process queued requests
        this.processQueue(null, accessToken);

        // Retry original request
        originalRequest.headers['Authorization'] = `Bearer ${accessToken}`;
        return this.service(originalRequest);
      } catch (refreshError) {
        // Refresh failed, clear auth and logout
        this.processQueue(refreshError, null);
        this.performLogout();
        return Promise.reject(refreshError);
      } finally {
        this.isRefreshing = false;
      }
    }

    // Handle other error codes
    switch (error.response?.status) {
      case 403:
        // 403 during normal user journey indicates a UI permission leak
        // The UI knows company + user permissions, so any unauthorized request
        // reaching the backend means a permission guard is missing
        console.warn('[PERMISSION LEAK] 403 Forbidden:', {
          url: error.config?.url,
          method: error.config?.method,
          message: 'UI sent unauthorized request - check permission guards',
        });
        break;
      case 404:
        // Not found
        break;
      case 409: {
        // AVA-602 GAP-15: explain a Tag save refused while its linked register's sync is set up.
        const pendingMessage = syncBaselinePendingMessage(error);
        if (pendingMessage) Vue.prototype.$dialog?.notify?.warning(pendingMessage, { timeout: 6000 });
        break;
      }
      default:
        // Internal server error
        break;
    }

    return Promise.reject({ ...error });
  };

  /**
   * Clear all authentication data
   */
  clearAuthData = () => {
    $removeFromLocal('accessToken');
    $removeFromLocal('refreshToken');
    $removeFromLocal('accessTokenExpiresAt');
    $removeFromLocal('tokenExpiresIn');
    $removeFromLocal('user');
    $removeFromCookies('token'); // Clear legacy cookie if exists
    delete this.service.defaults.headers['Authorization'];
  };

  /**
   * Perform logout with guard to prevent multiple calls
   */
  performLogout = () => {
    if (this.isLoggingOut) {
      return;
    }
    this.isLoggingOut = true;
    this.clearAuthData();
    window.vuexStore.dispatch('ACCOUNT/LOGOUT');
  };

  /**
   * Reset logout state (call after successful login)
   */
  resetLogoutState = () => {
    this.isLoggingOut = false;
  };

  /**
   * Cancel what a route navigation leaves behind (page requests, and facility requests when the
   * facility changes). Called by the router; see REQUEST_SCOPE in http/RequestRegistry.
   * @param {{ facilityChanged: boolean }} navigation
   * @returns {number} Number of requests cancelled
   */
  cancelOnNavigation = (navigation) => {
    return requestRegistry.cancelOnNavigation(navigation);
  };

  /**
   * Cancel all cancellable (non-critical) pending requests, whatever their scope
   * @returns {number} Number of requests cancelled
   */
  cancelAllCancellable = () => {
    return requestRegistry.cancelAllCancellable();
  };

  /**
   * Cancel ALL pending requests (including critical ones)
   * Use sparingly - mainly for logout or major state resets
   * @returns {number} Number of requests cancelled
   */
  cancelAll = () => {
    return requestRegistry.cancelAll();
  };

  /**
   * Get statistics about pending requests
   * Useful for debugging and monitoring
   * @returns {{ total: number, critical: number, cancellable: number }}
   */
  getPendingStats = () => {
    return requestRegistry.getStats();
  };

  redirectTo(url) {
    window.location.href = url;
  }

  get(config) {
    if (config instanceof String || typeof config === "string") {
      return this.service.get(config);
    }

    return this.service.request({ method: "GET", ...config });
    // return this.service.get(...args);
  }

  post(config) {
    return this.service.request({ method: "POST", ...config });
    // return this.service.post(...args);
  }

  put(config) {
    return this.service.request({ method: "PUT", ...config });
    // return this.service.put(...args);
  }

  patch(config) {
    return this.service.request({ method: "PATCH", ...config });
    // return this.service.patch(...args);
  }

  delete(config) {
    return this.service.request({ method: "DELETE", ...config });
    // return this.service.delete(...args);
  }

  request(...args) {
    return this.service.request(...args);
  }
}

export default new HttpService();
